import React, { useEffect, useState } from "react";

const getRandomStartPoint = () => {
  const w = window.innerWidth;
  const h = window.innerHeight;

  // Start at ~20% along diagonal from top-right to bottom-left
  // Diagonal 20% = roughly (80% width, 20% height)
  const startSide = Math.random() < 0.85 ? "diagonal-start" : "edges";

  if (startSide === "diagonal-start") {
    return {
      x: w * 0.75 + Math.random() * w * 0.3, // 75-105% width (some off-screen)
      y: h * 0.1 + Math.random() * h * 0.3, // 10-40% height
      angle: 130 + Math.random() * 10, // 130-140 degrees (closer to true diagonal)
    };
  } else {
    // Some from edges near the start area
    const edge = Math.random() < 0.5 ? "top" : "right";
    if (edge === "top") {
      return {
        x: w * 0.6 + Math.random() * w * 0.4, // Right side of top
        y: -50,
        angle: 125 + Math.random() * 15, // 125-140 degrees
      };
    } else {
      return {
        x: w + 50,
        y: Math.random() * h * 0.4, // Upper part of right edge
        angle: 135 + Math.random() * 10, // 135-145 degrees
      };
    }
  }
};

const AuroraBackground = ({
  children,
  showRadialGradient = true,
  minSpeed = 1,
  maxSpeed = 4,
  minDelay = 100,
  maxDelay = 2000,
  starColor = "#60a5fa",
  trailColor = "#93c5fd",
  starWidth = 10,
  starHeight = 4,
  darkMode = true,
  ...props
}) => {
  const [stars, setStars] = useState([]);

  // Consistent color palette
  const colors = {
    primary: darkMode ? "#60a5fa" : "#3b82f6",
    secondary: darkMode ? "#93c5fd" : "#60a5fa",
    accent: darkMode ? "#a5b4fc" : "#93c5fd",
    bg: darkMode ? "#18181b" : "#fafafa",
    text: darkMode ? "#f1f5f9" : "#0f172a",
  };

  // Create shooting stars more frequently
  useEffect(() => {
    const createStar = () => {
      const { x, y, angle } = getRandomStartPoint();
      const newStar = {
        id: Date.now() + Math.random(),
        x,
        y,
        angle,
        scale: 1.2,
        speed: Math.random() * (maxSpeed - minSpeed) + minSpeed,
        distance: 0,
      };
      setStars((prev) => {
        const next = [...prev, newStar];
        return next.length > 80 ? next.slice(-80) : next; // Fewer stars for calmer effect
      });
      const randomDelay = Math.random() * (maxDelay - minDelay) + minDelay;
      setTimeout(createStar, randomDelay);
    };
    createStar();
  }, [minSpeed, maxSpeed, minDelay, maxDelay]);

  // Move stars
  useEffect(() => {
    const moveStar = () => {
      setStars((prevStars) =>
        prevStars
          .map((star) => {
            const newX =
              star.x + star.speed * Math.cos((star.angle * Math.PI) / 180);
            const newY =
              star.y + star.speed * Math.sin((star.angle * Math.PI) / 180);
            const newDistance = star.distance + star.speed;
            const newScale = 1 + newDistance / 100;

            if (
              newX < -window.innerWidth * 0.2 ||
              newX > window.innerWidth * 1.2 ||
              newY < -window.innerHeight * 0.2 ||
              newY > window.innerHeight * 1.2
            ) {
              return null;
            }

            return {
              ...star,
              x: newX,
              y: newY,
              distance: newDistance,
              scale: newScale,
            };
          })
          .filter(Boolean)
      );
    };

    const animationFrame = requestAnimationFrame(moveStar);
    return () => cancelAnimationFrame(animationFrame);
  }, [stars]);

  const containerStyle = {
    position: "relative",
    display: "flex",
    height: "100vh",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.bg,
    color: colors.text,
    transition: "background-color 0.3s ease",
    overflow: "hidden",
  };

  const auroraKeyframes = `
    @keyframes aurora {
      0% {
        opacity: 0.3;
        transform: scale(1) rotate(0deg);
      }
      50% {
        opacity: 0.7;
        transform: scale(1.2) rotate(3deg);
      }
      100% {
        opacity: 0.3;
        transform: scale(1) rotate(0deg);
      }
    }

    @keyframes starField {
      0% {
        transform: translateX(0px) translateY(0px);
        opacity: 0;
      }
      20% {
        transform: translateX(-20px) translateY(20px);
        opacity: 1;
      }
      80% {
        transform: translateX(-80px) translateY(80px);
        opacity: 1;
      }
      100% {
        transform: translateX(-100px) translateY(100px);
        opacity: 0;
      }
    }

    @keyframes starFieldSlow {
      0% {
        transform: translateX(0px) translateY(0px);
      }
      100% {
        transform: translateX(-40px) translateY(40px);
      }
    }
  `;

  const auroraStyle = {
    position: "absolute",
    top: "-40%",
    right: "-40%",
    width: "90%",
    height: "90%",
    background: `radial-gradient(ellipse 80% 60% at 70% 30%, 
      ${colors.primary}60 0%, 
      ${colors.secondary}40 30%, 
      ${colors.accent}20 60%, 
      transparent 80%
    )`,
    filter: "blur(60px)",
    animation: "aurora 6s ease-in-out infinite",
    willChange: "transform, opacity",
  };

  const starFieldStyle = {
    position: "absolute",
    inset: 0,
    backgroundImage: `
      radial-gradient(1px 1px at 10% 20%, ${colors.primary}, transparent),
      radial-gradient(1px 1px at 80% 80%, ${colors.secondary}, transparent),
      radial-gradient(2px 2px at 90% 30%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 15% 90%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 20% 40%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 70% 70%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 40% 60%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 60% 10%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 30% 80%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 85% 50%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 5% 60%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 95% 90%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 50% 30%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 25% 70%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 75% 20%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 45% 85%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 65% 45%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 35% 65%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 35% 25%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 65% 75%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 45% 85%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 85% 15%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 15% 55%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 75% 45%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 55% 65%, ${colors.secondary}, transparent),
      radial-gradient(1px 1px at 25% 35%, ${colors.primary}, transparent),
      radial-gradient(2px 2px at 90% 70%, ${colors.accent}, transparent),
      radial-gradient(1px 1px at 10% 10%, ${colors.secondary}, transparent)
    `,
    opacity: 0.8,
    backgroundSize: "100% 100%",
    animation: "starField 20s linear infinite",
  };

  return (
    <main style={containerStyle} {...props}>
      <style>{auroraKeyframes}</style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Simplified single aurora effect */}
        <div style={auroraStyle} />

        {/* Enhanced multi-layer star fields */}
        <div style={starFieldStyle} />
      </div>

      {/* Shooting stars */}
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            position: "absolute",
            left: star.x,
            top: star.y,
            width: `${starWidth * star.scale}px`,
            height: `${starHeight}px`,
            background: `linear-gradient(90deg, ${colors.primary}, transparent)`,
            borderRadius: "50%",
            transform: `rotate(${star.angle + 180}deg)`,
            opacity: Math.max(0, 1 - star.distance / 300),
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Content */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {children}
      </div>
    </main>
  );
};

export default AuroraBackground;
